// src/pages/Shop.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Chip,
  Stack,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
  alpha,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Rating,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel
} from '@mui/material';
import Layout from '../components/Layout';
import { SearchIcon, CartIcon } from '../components/Icons';
import { getBestImageUrl, getPublicImageUrl } from '../services/s3Service';
import { InternalToolsProductService, SoldProduct, Order, CartItem } from '../types/marketTypes';

// Simple emoji icons
const CloseIcon = () => <Box component="span" sx={{ fontSize: '20px', display: 'flex', alignItems: 'center' }}>✕</Box>;
const AddIcon = () => <Box component="span" sx={{ fontSize: '20px', display: 'flex', alignItems: 'center' }}>+</Box>;
const RemoveIcon = () => <Box component="span" sx={{ fontSize: '20px', display: 'flex', alignItems: 'center' }}>−</Box>;
const DeleteIcon = () => <Box component="span" sx={{ fontSize: '20px', display: 'flex', alignItems: 'center' }}>×</Box>;

// API Configuration
const API_BASE_URL = 'https://internaltoolsapi-ahdj.onrender.com/api'; //API base URL
const S3_BASE_URL = 'https://s3.eu-west-1.amazonaws.com/products-services-media';
const ORG_ID = 'zendi001'; // Zendi's organization ID



// Category mapping based on product tags
const categoryMapping: { [key: string]: string } = {
  'point-of-sale': 'POS Hardware',
  'hardware': 'POS Hardware',
  'pos': 'POS Hardware',
  'healthcare': 'Healthcare Equipment',
  'point-of-care': 'Healthcare Equipment',
  'medical': 'Healthcare Equipment',
  'accessories': 'Accessories',
  'printer': 'Accessories',
  'scanner': 'Accessories',
  'terminal': 'Accessories'
};

const Shop: React.FC = () => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [products, setProducts] = useState<InternalToolsProductService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orderPlacing, setOrderPlacing] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string>('Cash on Delivery');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  
  // Notification states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info'
  });

  const [imageUrls, setImageUrls] = useState<{[key: string]: string}>({});

  // Fetch products on component mount
useEffect(() => {
  fetchProducts();
}, []);

// Load images when products change
useEffect(() => {
  const loadImages = async () => {
    if (products.length > 0) {
      const urls: {[key: string]: string} = {};
      
      // Load images for each product
      await Promise.all(
        products.map(async (product) => {
          const url = await getImageUrl(product);
          urls[product.uniqueProductId] = url;
        })
      );
      
      setImageUrls(urls);
    }
  };
  
  if (!loading && products.length > 0) {
    loadImages();
  }
}, [products, loading]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/ProductsServices/GetOrgOnlineProductsServices/${ORG_ID}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch products: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Filter only products with onlineListedFlag = true
      const onlineProducts = data.filter((product: InternalToolsProductService) => 
        product.onlineListedFlag === true && product.productAvailabilityFlag === true
      );
      
      setProducts(onlineProducts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products');
      console.error('Error fetching products:', err);
      
      // Fallback to mock data for development
      setProducts(getMockProducts());
    } finally {
      setLoading(false);
    }
  };

  // Helper to get full image URL

  const getImageUrl = async (product: InternalToolsProductService): Promise<string> => {
  // Try productPictures array first
  if (product.productPictures && product.productPictures.length > 0) {
    const imageKey = product.productPictures[0];
    if (imageKey) {
      try {
        // Use the S3 service to get the best available URL
        const url = await getBestImageUrl(imageKey);
        return url;
      } catch (error) {
        console.error('Failed to get S3 URL for product:', product.productName, error);
      }
    }
  }
  
  // Default fallback image
  return '';
};
  // Get product category based on tags
  const getProductCategory = (product: InternalToolsProductService): string => {
    if (!product.productTag) return 'Other';
    
    const tags = product.productTag.toLowerCase().split(',').map(tag => tag.trim());
    
    for (const tag of tags) {
      if (categoryMapping[tag]) {
        return categoryMapping[tag];
      }
    }
    
    // Check for partial matches
    for (const [key, value] of Object.entries(categoryMapping)) {
      if (tags.some(tag => tag.includes(key) || key.includes(tag))) {
        return value;
      }
    }
    
    return 'Other';
  };

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.productDescription.toLowerCase().includes(searchTerm.toLowerCase());
    const productCategory = getProductCategory(product);
    const matchesCategory = selectedCategory === 'all' || productCategory === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Cart functions
  const addToCart = (product: InternalToolsProductService) => {
  // Get image from preloaded URLs or use fallback
  const productImage = imageUrls[product._id] || 
                      (product.productPictures && product.productPictures.length > 0 
                        ? getPublicImageUrl(product.productPictures[0])
                        : '');
  
  setCart(prevCart => {
    const existingItem = prevCart.find(item => item.productId === product._id);
    if (existingItem) {
      return prevCart.map(item =>
        item.productId === product._id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
    } else {
      return [...prevCart, {
        productId: product._id,
        uniqueProductId: product.uniqueProductId,
        productName: product.productName,
        productPriceInUSD: product.productPriceInUSD,
        quantity: 1,
        productImage: productImage, // Use the string URL
        quantityMetric: product.quantityMetric,
        productType: product.productType,
        productDescription: product.productDescription
      }];
    }
  });
  
  showNotification('Product added to cart', 'success');
};

  const removeFromCart = (productId: string) => {
    setCart(prevCart => prevCart.filter(item => item.productId !== productId));
    showNotification('Product removed from cart', 'info');
  };

  const updateQuantity = (productId: string, newQuantity: number) => {
    if (newQuantity < 1) {
      removeFromCart(productId);
      return;
    }
    
    setCart(prevCart =>
      prevCart.map(item =>
        item.productId === productId
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  const cartTotal = cart.reduce((total, item) => total + (item.productPriceInUSD * item.quantity), 0);
  const cartItemsCount = cart.reduce((count, item) => count + item.quantity, 0);

  // Generate order ID
  const generateOrderId = (): string => {
    const timestamp = new Date().getTime();
    const random = Math.floor(Math.random() * 10000);
    return `ORD-${timestamp}-${random}`;
  };

  // Place order - UPDATED VERSION
const placeOrder = async () => {
  if (cart.length === 0) {
    showNotification('Cart is empty', 'error');
    return;
  }

  if (!deliveryAddress.trim()) {
    showNotification('Please enter delivery address', 'error');
    return;
  }

  setOrderPlacing(true);
  setOrderError(null);

  try {
    // Convert cart items to SoldProduct array - FIXED FORMAT
    const cartList: SoldProduct[] = cart.map(item => {
      // Find the original product to get wellnessProductId
      const originalProduct = products.find(p => p._id === item.productId);
      
      return {
        wellnessProductId: '',
        uniqueProductId: item.uniqueProductId,
        wellnessProductTag: originalProduct?.productTag || 'zendi-shop',
        productName: item.productName,
        provider: 'zendi', // Changed from 'Zendi' to lowercase
        productPrice: item.productPriceInUSD,
        productDescription: item.productDescription,
        officialTimeTakenInMins: 0,
        variation: 'standard',
        wellnessPillar: '', // Updated value
        productType: item.productType,
        payers: [],
        productQuantityBought: item.quantity,
        quantityMetric: item.quantityMetric,
        valueAddedTaxOrEquivalentPercentage: originalProduct?.valueAddedTaxOrEquivalentPercentage || 0.15
      };
    });

    // Create order object with EXACT schema
    const order: Order = {
      orderId: generateOrderId(),
      orgUniqueId: ORG_ID,
      orderStatus: 'pending',
      customerUserName: 'guest-customer', // Can't be empty
      providerUserName: 'zendi', // lowercase
      orderDateTimeStamp: new Date().toISOString(),
      paymentStatus: 'pending',
      paymentMethodPreferred: paymentMethod,
      saleUniqueId: ``,
      orderPriority: 'normal',
      deliveryAddress: deliveryAddress,
      deliveryDateTimeStamp: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days from now
      deliveryStatus: 'scheduled',
      cartList: cartList
    };

    // Debug: Log the payload before sending
    console.log('Order payload:', JSON.stringify(order, null, 2));

    // Send order to API
    const response = await fetch(`${API_BASE_URL}/Services/PlaceOrder`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(order)
    });

    // Try to get detailed error info
    if (!response.ok) {
      let errorDetails = '';
      try {
        const errorData = await response.json();
        errorDetails = JSON.stringify(errorData);
      } catch (e) {
        errorDetails = await response.text();
      }
      throw new Error(`Failed to place order: ${response.status} ${response.statusText}. Details: ${errorDetails}`);
    }

    const placedOrder = await response.json();
    console.log('Order placed successfully:', placedOrder);
    
    // Clear cart and show success
    setCart([]);
    setOrderSuccess(true);
    setCheckoutOpen(false);
    showNotification('Order placed successfully! Check your account to track order.', 'success');
    
    // Reset after 3 seconds
    setTimeout(() => {
      setOrderSuccess(false);
    }, 3000);

  } catch (err) {
    const errorMsg = err instanceof Error ? err.message : 'Failed to place order';
    setOrderError(errorMsg);
    showNotification(`Failed to place order: ${errorMsg}`, 'error');
    console.error('Error placing order:', err);
  } finally {
    setOrderPlacing(false);
  }
};

  // Show notification
  const showNotification = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Get unique categories from products
  const getCategories = () => {
    const categoriesSet = new Set<string>();
    products.forEach(product => {
      categoriesSet.add(getProductCategory(product));
    });
    return Array.from(categoriesSet);
  };

  // Mock data for development fallback
  const getMockProducts = (): InternalToolsProductService[] => {
    return [
      {
        _id: '1',
        uniqueProductId: 'ZP001',
        orgUniqueId: ORG_ID,
        uniqueBarCode: '123456789',
        wellnessProductId: 'WP001',
        officialProductPictureUri: 'images/PEP1103212556ZEN_0104012837_image_0_JPEG_20260104_012727_3692841987404697051.jpg',
        regionUniqueTaxId: 'KE001',
        productName: 'Zendi RTE Pro Kit',
        productType: 'Product',
        productPriceInUSD: 899.99,
        productDescription: 'Complete POS kit including tablet, thermal printer, cash drawer, barcode scanner, and card terminal.',
        productTag: 'point-of-sale,hardware,pos',
        quantityAvailable: 156,
        quantityMetric: 'kits',
        valueAddedTaxOrEquivalentPercentage: 0.15,
        productAvailabilityFlag: true,
        onlineListedFlag: true,
        inputsList: [],
        encodedSalesList: [],
        encodedPurchasesList: [],
        productPictures: ['images/PEP1103212556ZEN_0104012837_image_0_JPEG_20260104_012727_3692841987404697051.jpg'],
        variations: []
      },
      {
        _id: '2',
        uniqueProductId: 'ZP002',
        orgUniqueId: ORG_ID,
        uniqueBarCode: '123456790',
        wellnessProductId: 'WP002',
        officialProductPictureUri: '',
        regionUniqueTaxId: 'KE001',
        productName: 'Thermal Receipt Printer',
        productType: 'Product',
        productPriceInUSD: 249.99,
        productDescription: 'High-speed thermal receipt printer with Bluetooth and USB connectivity.',
        productTag: 'printer,accessories,hardware',
        quantityAvailable: 289,
        quantityMetric: 'units',
        valueAddedTaxOrEquivalentPercentage: 0.15,
        productAvailabilityFlag: true,
        onlineListedFlag: true,
        inputsList: [],
        encodedSalesList: [],
        encodedPurchasesList: [],
        productPictures: [],
        variations: []
      },
      {
        _id: '3',
        uniqueProductId: 'ZP003',
        orgUniqueId: ORG_ID,
        uniqueBarCode: '123456791',
        wellnessProductId: 'WP003',
        officialProductPictureUri: '',
        regionUniqueTaxId: 'KE001',
        productName: 'Wireless Barcode Scanner',
        productType: 'Product',
        productPriceInUSD: 149.99,
        productDescription: '2D wireless barcode scanner with 10-hour battery life and 10-meter range.',
        productTag: 'scanner,accessories,point-of-sale',
        quantityAvailable: 423,
        quantityMetric: 'units',
        valueAddedTaxOrEquivalentPercentage: 0.15,
        productAvailabilityFlag: true,
        onlineListedFlag: true,
        inputsList: [],
        encodedSalesList: [],
        encodedPurchasesList: [],
        productPictures: [],
        variations: []
      },
      {
        _id: '4',
        uniqueProductId: 'ZP004',
        orgUniqueId: ORG_ID,
        uniqueBarCode: '123456792',
        wellnessProductId: 'WP004',
        officialProductPictureUri: '',
        regionUniqueTaxId: 'KE001',
        productName: 'Mobile Card Terminal',
        productType: 'Product',
        productPriceInUSD: 199.99,
        productDescription: 'Portable card terminal supporting contactless, chip, and swipe payments.',
        productTag: 'terminal,accessories,point-of-sale',
        quantityAvailable: 312,
        quantityMetric: 'units',
        valueAddedTaxOrEquivalentPercentage: 0.15,
        productAvailabilityFlag: true,
        onlineListedFlag: true,
        inputsList: [],
        encodedSalesList: [],
        encodedPurchasesList: [],
        productPictures: [],
        variations: []
      },
      {
        _id: '5',
        uniqueProductId: 'ZP005',
        orgUniqueId: ORG_ID,
        uniqueBarCode: '123456793',
        wellnessProductId: 'WP005',
        officialProductPictureUri: '',
        regionUniqueTaxId: 'KE001',
        productName: 'MDE Basic Kit for Clinics',
        productType: 'Product',
        productPriceInUSD: 2999.99,
        productDescription: 'Complete medical diagnostic equipment kit for small clinics.',
        productTag: 'healthcare,point-of-care,medical',
        quantityAvailable: 45,
        quantityMetric: 'units',
        valueAddedTaxOrEquivalentPercentage: 0.15,
        productAvailabilityFlag: true,
        onlineListedFlag: true,
        inputsList: [],
        encodedSalesList: [],
        encodedPurchasesList: [],
        productPictures: [],
        variations: []
      }
    ];
  };

  // Categories for filtering
  const categories = [
    { id: 'all', name: 'All Products', count: products.length },
    ...getCategories().map(category => ({
      id: category,
      name: category,
      count: products.filter(p => getProductCategory(p) === category).length
    }))
  ];

  // Payment methods
  const paymentMethods = [
    { id: 'Cash on Delivery', label: 'Cash on Delivery', enabled: true },
    { id: 'PayPal', label: 'PayPal', enabled: false },
    { id: 'Ecocash', label: 'Ecocash', enabled: false },
    { id: 'PayNow', label: 'PayNow', enabled: false },
    { id: 'Credit Card', label: 'Credit Card', enabled: false }
  ];

  return (
    <Layout cartItemsCount={cartItemsCount}>
      <Box sx={{ flexGrow: 1, py: 4 }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              bgcolor: 'transparent',
              borderRadius: 0,
              p: { xs: 0, md: 0 },
              mb: 6,
              textAlign: 'left',
              border: 'none',
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'primary.dark',
                mb: 1.5,
              }}
            >
              Hardware
            </Typography>
            <Typography variant="h1" sx={{ 
              fontWeight: 700, 
              mb: 2,
              fontSize: { xs: '2.4rem', md: '3.25rem' },
              letterSpacing: '-0.04em',
            }}>
              Shop
            </Typography>
            <Typography sx={{ 
              color: 'text.secondary', 
              mb: 4,
              maxWidth: 560,
              fontSize: 17,
            }}>
              Hardware Zendi currently sells — devices and kits for clinics and operations.
            </Typography>
            
            {/* Search Bar */}
            <Box sx={{ maxWidth: 520, mb: 3 }}>
              <TextField
                fullWidth
                placeholder="Search for products, equipment, or kits..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 3, bgcolor: 'background.paper' }
                }}
              />
            </Box>

            {/* Category Chips */}
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1, 
            }}>
              {categories.map((category) => (
                <Chip
                  key={category.id}
                  label={`${category.name} (${category.count})`}
                  onClick={() => setSelectedCategory(category.id)}
                  color={selectedCategory === category.id ? 'primary' : 'default'}
                  sx={{ 
                    mb: 1,
                    fontWeight: selectedCategory === category.id ? 600 : 400
                  }}
                />
              ))}
            </Box>
          </Paper>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 4 }}>
              {error} - Using demo data. Please check your API connection.
            </Alert>
          )}

          {/* Shop Content */}
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            margin: -2 
          }}>
            {/* Sidebar Filters */}
            <Box sx={{ 
              width: { xs: '100%', md: '25%' }, 
              padding: 2,
              position: 'relative'
            }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  mb: 3,
                  position: 'sticky',
                  top: 20
                }}
              >
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Filters
                </Typography>

                {/* Category Filter */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Category
                  </Typography>
                  <Stack spacing={1}>
                    {categories.map((category) => (
                      <Button
                        key={category.id}
                        fullWidth
                        onClick={() => setSelectedCategory(category.id)}
                        variant={selectedCategory === category.id ? 'contained' : 'outlined'}
                        size="small"
                        sx={{ 
                          justifyContent: 'flex-start',
                          textTransform: 'none'
                        }}
                      >
                        {category.name} ({category.count})
                      </Button>
                    ))}
                  </Stack>
                </Box>

                {/* Price Range */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Stock Status
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Only in-stock products are shown
                  </Typography>
                </Box>
              </Paper>

              {/* Cart Summary */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  bgcolor: alpha(theme.palette.primary.light, 0.05)
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Cart Summary
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Items: {cartItemsCount}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, fontWeight: 600 }}>
                  Total: ${cartTotal.toFixed(2)}
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<CartIcon />}
                  onClick={() => setCartOpen(true)}
                  disabled={cart.length === 0}
                >
                  View Cart ({cartItemsCount})
                </Button>
              </Paper>
            </Box>

            {/* Product Grid */}
            <Box sx={{ 
              width: { xs: '100%', md: '75%' }, 
              padding: 2 
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {selectedCategory === 'all' ? 'All Products' : selectedCategory}
                  <Typography component="span" sx={{ ml: 2, color: 'text.secondary' }}>
                    ({filteredProducts.length} items)
                  </Typography>
                </Typography>
                
                {loading && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography variant="body2">Loading products...</Typography>
                  </Box>
                )}
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                  <CircularProgress />
                </Box>
              ) : filteredProducts.length === 0 ? (
                <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    No products found
                  </Typography>
                  <Typography color="text.secondary">
                    Try adjusting your search or filters
                  </Typography>
                </Paper>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap',
                  margin: -2
                }}>
                  {filteredProducts.map((product) => (
                    <Box key={product._id} sx={{ 
                      width: { xs: '100%', sm: '50%', lg: '33.333%' }, 
                      padding: 2 
                    }}>
                      <Card
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'all 0.3s ease',
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: theme.shadows[8],
                            borderColor: theme.palette.primary.light
                          }
                        }}
                      >
                        {/* Product Image */}
                        <Box sx={{ position: 'relative', pt: '75%' }}>
                          <CardMedia
                            component="img"
  image={imageUrls[product.uniqueProductId] || getPublicImageUrl(product.productPictures?.[0] || '')}
  alt={product.productName}
  onError={(e) => {
    const target = e.target as HTMLImageElement;
    console.error('Failed to load image:', target.src);
    target.src = '';
  }}
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                          <Chip
                            label={getProductCategory(product)}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 12,
                              left: 12,
                              bgcolor: getProductCategory(product) === 'Healthcare Equipment' 
                                ? alpha(theme.palette.secondary.main, 0.9)
                                : alpha(theme.palette.primary.main, 0.9),
                              color: 'white',
                              fontWeight: 600
                            }}
                          />
                          {product.quantityAvailable < 10 && (
                            <Chip
                              label="Low Stock"
                              size="small"
                              color="warning"
                              sx={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                fontWeight: 600
                              }}
                            />
                          )}
                        </Box>

                        <CardContent sx={{ flexGrow: 1, p: 3 }}>
                          <Typography variant="h6" sx={{ 
                            fontWeight: 700, 
                            mb: 1,
                            height: '3rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}>
                            {product.productName}
                          </Typography>

                          {product.productTag && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                              {product.productTag.split(',').map((tag, idx) => (
                                <Chip
                                  key={idx}
                                  label={tag.trim()}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    color: theme.palette.primary.main,
                                    fontSize: '0.6rem',
                                    height: 20
                                  }}
                                />
                              ))}
                            </Box>
                          )}

                          <Typography variant="body2" color="text.secondary" sx={{ 
                            mb: 2,
                            height: '3rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}>
                            {product.productDescription}
                          </Typography>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                              ${product.productPriceInUSD.toFixed(2)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Stock: {product.quantityAvailable} {product.quantityMetric}
                            </Typography>
                          </Box>
                        </CardContent>

                        <CardActions sx={{ p: 3, pt: 0 }}>
                          <Button
                            fullWidth
                            variant="contained"
                            startIcon={<CartIcon />}
                            onClick={() => addToCart(product)}
                            disabled={product.quantityAvailable === 0}
                            sx={{
                              fontWeight: 600,
                              py: 1.2
                            }}
                          >
                            {product.quantityAvailable === 0 ? 'Out of Stock' : 'Add to Cart'}
                          </Button>
                        </CardActions>
                      </Card>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>

          {/* Order Success Alert */}
          {orderSuccess && (
            <Alert 
              severity="success" 
              sx={{ 
                mt: 4,
                position: 'fixed',
                top: 20,
                right: 20,
                zIndex: 9999,
                width: 400
              }}
              onClose={() => setOrderSuccess(false)}
            >
              Order placed successfully! Check your account to track order.
            </Alert>
          )}
        </Container>
      </Box>

      {/* Shopping Cart Drawer */}
      <Drawer
        anchor="right"
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 400 } }
        }}
      >
        <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Shopping Cart
            </Typography>
            <IconButton onClick={() => setCartOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {cart.length === 0 ? (
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{ fontSize: 80, color: 'text.secondary', mb: 2, opacity: 0.35, display: 'flex', justifyContent: 'center' }}>
                <CartIcon size={56} />
              </Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Your cart is empty
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Add some products to get started
              </Typography>
              <Button
                variant="contained"
                onClick={() => setCartOpen(false)}
              >
                Continue Shopping
              </Button>
            </Box>
          ) : (
            <>
              <List sx={{ flexGrow: 1, overflow: 'auto' }}>
                {cart.map((item) => (
                  <React.Fragment key={item.productId}>
                    <ListItem sx={{ px: 0 }}>
                      <Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
                        <Box sx={{ 
                          width: 80, 
                          height: 80, 
                          borderRadius: 2,
                          overflow: 'hidden',
                          flexShrink: 0
                        }}>
                          <img 
                            src={imageUrls[item.uniqueProductId] || item.productImage} 
                            alt={item.productName}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = '';
                            }}
                          />
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {item.productName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            ${item.productPriceInUSD.toFixed(2)} each
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <IconButton 
                                size="small" 
                                onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                              >
                                <RemoveIcon />
                              </IconButton>
                              <Typography sx={{ minWidth: 30, textAlign: 'center' }}>
                                {item.quantity}
                              </Typography>
                              <IconButton 
                                size="small" 
                                onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                              >
                                <AddIcon />
                              </IconButton>
                            </Box>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              ${(item.productPriceInUSD * item.quantity).toFixed(2)}
                            </Typography>
                            <IconButton 
                              size="small" 
                              onClick={() => removeFromCart(item.productId)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>

              {/* Cart Summary */}
              <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography>Subtotal</Typography>
                  <Typography>${cartTotal.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Total</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>${cartTotal.toFixed(2)}</Typography>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={() => {
                    setCartOpen(false);
                    setCheckoutOpen(true);
                  }}
                  sx={{ 
                    mb: 2,
                    py: 1.5,
                    fontWeight: 600,
                    bgcolor: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark
                    }
                  }}
                >
                  Proceed to Checkout
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setCartOpen(false)}
                >
                  Continue Shopping
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Drawer>

      {/* Checkout Dialog */}
      <Dialog
        open={checkoutOpen}
        onClose={() => !orderPlacing && setCheckoutOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Checkout
        </DialogTitle>
        
        <DialogContent>
          {orderError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {orderError}
            </Alert>
          )}
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Order Summary
            </Typography>
            
            <List dense>
              {cart.map((item) => (
                <ListItem key={item.productId} sx={{ px: 0 }}>
                  <ListItemText 
                    primary={item.productName}
                    secondary={`${item.quantity} × $${item.productPriceInUSD.toFixed(2)}`}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ${(item.productPriceInUSD * item.quantity).toFixed(2)}
                  </Typography>
                </ListItem>
              ))}
            </List>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Total</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>${cartTotal.toFixed(2)}</Typography>
            </Box>
            
            {/* Delivery Address */}
            <TextField
              fullWidth
              label="Delivery Address"
              value={deliveryAddress}
              onChange={(e) => setDeliveryAddress(e.target.value)}
              multiline
              rows={2}
              required
              sx={{ mb: 3 }}
              placeholder="Enter your full delivery address"
            />
            
            {/* Payment Method */}
            <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
              <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                Payment Method
              </FormLabel>
              <RadioGroup
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                {paymentMethods.map((method) => (
                  <FormControlLabel
                    key={method.id}
                    value={method.id}
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {method.label}
                        {!method.enabled && (
                          <Chip
                            label="Coming Soon"
                            size="small"
                            sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    }
                    disabled={!method.enabled}
                  />
                ))}
              </RadioGroup>
            </FormControl>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              Only Cash on Delivery is available at the moment. Other payment methods coming soon.
            </Alert>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => setCheckoutOpen(false)}
            disabled={orderPlacing}
          >
            Cancel
          </Button>
          
          <Button
            variant="contained"
            onClick={placeOrder}
            disabled={orderPlacing || !deliveryAddress.trim()}
            sx={{
              bgcolor: theme.palette.primary.main,
              '&:hover': { bgcolor: theme.palette.primary.dark }
            }}
          >
            {orderPlacing ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                Placing Order...
              </>
            ) : 'Place Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default Shop;